import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { withNamespaces } from 'react-i18next';

import { MapboxMap } from '../../../views/map/MapboxMapsProvider';
import ErrorBoundary from '../../ErrorBoundary';
import MapLoader from '../../MapLoader';
import Text from '../../Text';
import GenericModal from '../GenericModal';
import classNames from './style.module.scss';

/**
 * Modal to display the Trip's data on Full Screen
 */
const FullScreenTripModal = props => {
  const { t, mapConfig, visible, onCancel, ...rest } = props;

  const [currentRequest, setCurrentRequest] = useState();
  const [isLoadingMapDetails, setIsLoadingMapDetails] = useState(false);
  const [detailedMapConfig, setDetailedMapConfig] = useState();
  const [routeVisibility, setRouteVisibility] = useState(
    mapConfig?.initialRouteVisibility || { recorded: true, adjusted: true },
  );

  const resetState = () => {
    setCurrentRequest();
    setIsLoadingMapDetails(false);
    setDetailedMapConfig();
    setRouteVisibility({ recorded: true, adjusted: true });
  };

  useEffect(() => {
    if (visible) {
      setDetailedMapConfig(mapConfig);
      setIsLoadingMapDetails(false);
      if (mapConfig?.initialRouteVisibility) {
        setRouteVisibility(mapConfig.initialRouteVisibility);
      }
    } else if (isLoadingMapDetails && currentRequest) {
      currentRequest.cancel();
      resetState();
    } else {
      resetState();
    }
    // eslint-disable-next-line
  }, [visible, mapConfig]);

  return (
    <GenericModal
      {...rest}
      centered
      destroyOnClose
      footer={null}
      width={'100vw'}
      className={classNames.tripsModal}
      visible={visible}
      onCancel={onCancel}
    >
      <div className={classNames.mapHeader}>
        {mapConfig.date && (
          <Text variant="p" size="sm">
            <b>{t('date')}:</b> {mapConfig.date}
          </Text>
        )}

        {mapConfig.toLoc && (
          <Text variant="p" size="sm">
            <b>{t('from')}:</b> {mapConfig.fromLoc}
          </Text>
        )}

        {mapConfig.fromLoc && (
          <Text variant="p" size="sm">
            <b>{t('to')}:</b> {mapConfig.toLoc}
          </Text>
        )}

        {detailedMapConfig?.routes?.length > 1 && (
          <div className={classNames.routeLegend}>
            <div
              className={`${classNames.legendItem} ${
                !routeVisibility.recorded ? classNames.inactive : ''
              }`}
              onClick={() => setRouteVisibility(prev => ({ ...prev, recorded: !prev.recorded }))}
            >
              <div className={classNames.legendContent}>
                <div className={`${classNames.legendLine} ${classNames.recorded}`} />
                <span>{t('recordedTrip')}</span>
              </div>
              {routeVisibility.recorded ? (
                <EyeOutlined className={classNames.eyeIcon} />
              ) : (
                <EyeInvisibleOutlined className={classNames.eyeIcon} />
              )}
            </div>
            <div
              className={`${classNames.legendItem} ${
                !routeVisibility.adjusted ? classNames.inactive : ''
              }`}
              onClick={() => setRouteVisibility(prev => ({ ...prev, adjusted: !prev.adjusted }))}
            >
              <div className={classNames.legendContent}>
                <div className={`${classNames.legendLine} ${classNames.adjusted}`} />
                <span>{t('adjustedTrip')}</span>
              </div>
              {routeVisibility.adjusted ? (
                <EyeOutlined className={classNames.eyeIcon} />
              ) : (
                <EyeInvisibleOutlined className={classNames.eyeIcon} />
              )}
            </div>
          </div>
        )}
      </div>

      <div className={classNames.mapWrapper}>
        {isLoadingMapDetails || !detailedMapConfig ? (
          <MapLoader />
        ) : (
          <ErrorBoundary t={t} fallback={<MapLoader text={t('errorLoadingMap')} />}>
            <MapboxMap
              key={mapConfig.tripId}
              config={{ zoom: 10, center: detailedMapConfig.center }}
              routes={detailedMapConfig.routes}
              routeVisibility={detailedMapConfig.routes.length > 1 ? routeVisibility : undefined}
            />
          </ErrorBoundary>
        )}
      </div>
    </GenericModal>
  );
};

FullScreenTripModal.propTypes = {
  mapConfig: PropTypes.shape({}).isRequired,
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default withNamespaces()(FullScreenTripModal);
